/**
 * Core Cryptography Service for Customer App
 *
 * Provides zero-knowledge decryption primitives for viewing consultant-encrypted data.
 * Uses Web Crypto API for AES-256-GCM decryption.
 *
 * Security Features:
 * - AES-256-GCM (authenticated decryption)
 * - HMAC-SHA256 (blind index verification)
 * - Constant-time string comparison
 *
 * NOTE: This service is READ-ONLY. Customers cannot encrypt data.
 * All data is encrypted by the consultant and decrypted here.
 */

import type { EncryptedData } from '@/types/crypto'
import { AES_CONSTANTS } from '@/types/crypto'

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Convert ArrayBuffer to Base64 string
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * Convert Base64 string to ArrayBuffer
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * Convert Uint8Array to Base64 string
 */
export function uint8ArrayToBase64(array: Uint8Array): string {
  return arrayBufferToBase64(array.buffer as ArrayBuffer)
}

/**
 * Convert Base64 string to Uint8Array
 */
export function base64ToUint8Array(base64: string): Uint8Array {
  return new Uint8Array(base64ToArrayBuffer(base64))
}

/**
 * Convert Uint8Array to hex string
 */
export function uint8ArrayToHex(array: Uint8Array): string {
  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Convert hex string to Uint8Array
 */
export function hexToUint8Array(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16)
  }
  return bytes
}

// ============================================================================
// CRYPTO SERVICE CLASS
// ============================================================================

/**
 * Core Cryptography Service for Customer App (Read-Only)
 */
class CryptoService {
  /**
   * Decrypt data with the DEK (Data Encryption Key) using AES-256-GCM
   *
   * @param encryptedData - Encrypted data with ciphertext and IV
   * @param dek - Data Encryption Key (32 bytes)
   * @returns Decrypted and JSON-parsed plaintext
   */
  async decrypt<T>(encryptedData: EncryptedData, dek: Uint8Array): Promise<T> {
    // Import DEK as CryptoKey
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      dek as BufferSource,
      { name: AES_CONSTANTS.ALGORITHM, length: AES_CONSTANTS.KEY_LENGTH },
      false,
      ['decrypt']
    )

    // Decrypt data
    const decrypted = await crypto.subtle.decrypt(
      {
        name: AES_CONSTANTS.ALGORITHM,
        iv: base64ToUint8Array(encryptedData.iv),
      } as AesGcmParams,
      cryptoKey,
      base64ToArrayBuffer(encryptedData.ciphertext)
    )

    // Convert bytes back to JSON
    const decryptedString = new TextDecoder().decode(decrypted)
    return JSON.parse(decryptedString) as T
  }

  /**
   * Decrypt raw bytes (without JSON parsing)
   *
   * @param encryptedData - Encrypted data with ciphertext and IV
   * @param dek - Data Encryption Key (32 bytes)
   * @returns Decrypted bytes as Uint8Array
   */
  async decryptRaw(encryptedData: EncryptedData, dek: Uint8Array): Promise<Uint8Array> {
    // Import DEK as CryptoKey
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      dek as BufferSource,
      { name: AES_CONSTANTS.ALGORITHM, length: AES_CONSTANTS.KEY_LENGTH },
      false,
      ['decrypt']
    )

    // Decrypt data
    const decrypted = await crypto.subtle.decrypt(
      {
        name: AES_CONSTANTS.ALGORITHM,
        iv: base64ToUint8Array(encryptedData.iv),
      } as AesGcmParams,
      cryptoKey,
      base64ToArrayBuffer(encryptedData.ciphertext)
    )

    return new Uint8Array(decrypted)
  }

  /**
   * Generate HMAC-SHA256 for blind index verification
   *
   * Used to verify blind indexes match expected values without exposing the actual data.
   *
   * @param value - Value to hash (e.g., email, phone)
   * @param key - Key for HMAC
   * @returns Base64-encoded HMAC hash
   */
  async generateHMAC(value: string, key: Uint8Array): Promise<string> {
    // Normalize value (lowercase, trim whitespace)
    const normalizedValue = value.toLowerCase().trim()

    // Import key as CryptoKey for HMAC
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      key as BufferSource,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    )

    // Generate HMAC
    const valueBytes = new TextEncoder().encode(normalizedValue)
    const hmac = await crypto.subtle.sign('HMAC', cryptoKey, valueBytes)

    return arrayBufferToBase64(hmac)
  }

  /**
   * Compute SHA-256 hash of data
   *
   * @param data - Data to hash (string or Uint8Array)
   * @returns SHA-256 hash as Uint8Array
   */
  async sha256(data: string | Uint8Array): Promise<Uint8Array> {
    const dataBytes = typeof data === 'string' ? new TextEncoder().encode(data) : data
    const hash = await crypto.subtle.digest('SHA-256', this.toArrayBuffer(dataBytes))
    return new Uint8Array(hash)
  }

  /**
   * Constant-time string comparison
   *
   * Prevents timing attacks by ensuring comparison takes
   * the same time regardless of where strings differ.
   *
   * @param a - First string
   * @param b - Second string
   * @returns true if strings are equal
   */
  constantTimeEqual(a: string, b: string): boolean {
    if (a.length !== b.length) {
      // Still do comparison to maintain constant time (security: prevents timing attacks)
      /* eslint-disable @typescript-eslint/no-unused-vars */
      let _result = 0
      for (let i = 0; i < a.length; i++) {
        _result |= a.charCodeAt(i) ^ (b.charCodeAt(i % b.length) || 0)
      }
      /* eslint-enable @typescript-eslint/no-unused-vars */
      return false
    }

    let result = 0
    for (let i = 0; i < a.length; i++) {
      result |= a.charCodeAt(i) ^ b.charCodeAt(i)
    }
    return result === 0
  }

  /**
   * Constant-time Uint8Array comparison
   *
   * @param a - First array
   * @param b - Second array
   * @returns true if arrays are equal
   */
  constantTimeEqualBytes(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) {
      // Still do comparison to maintain constant time (security: prevents timing attacks)
      /* eslint-disable @typescript-eslint/no-unused-vars */
      let _result = 0
      for (let i = 0; i < a.length; i++) {
        _result |= a[i] ^ (b[i % b.length] || 0)
      }
      /* eslint-enable @typescript-eslint/no-unused-vars */
      return false
    }

    let result = 0
    for (let i = 0; i < a.length; i++) {
      result |= a[i] ^ b[i]
    }
    return result === 0
  }

  /**
   * Check if a string is valid Base64
   */
  isValidBase64(str: string): boolean {
    try {
      return btoa(atob(str)) === str
    } catch {
      return false
    }
  }

  /**
   * Generate random bytes (for nonces, IVs, etc.)
   *
   * @param length - Number of bytes to generate
   * @returns Random bytes as Uint8Array
   */
  getRandomBytes(length: number): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(length))
  }

  /**
   * Convert Uint8Array to ArrayBuffer (for Web Crypto API compatibility)
   *
   * This handles the case where Uint8Array.buffer could be SharedArrayBuffer
   */
  toArrayBuffer(data: Uint8Array): ArrayBuffer {
    // Create a new ArrayBuffer and copy the data
    const buffer = new ArrayBuffer(data.byteLength)
    new Uint8Array(buffer).set(data)
    return buffer
  }
}

// Export singleton instance
export const cryptoService = new CryptoService()
